import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Question } from "../../models/Question";
import { AdminQuestionService } from "../../services/admin/AdminQuestionService";
import { useAuth } from "../AuthContext";
import { getSubdomainFromUrl } from "../../utils/url";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface AdminQuestionContextProps {
  questions: Question[];
  selectedQuestion: Question | null;
  setSelectedQuestion: (questionId: string) => void;
  createQuestion: (
    questionData: Omit<
      Question,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter" | "updatedBy"
    >,
  ) => Promise<string>;
  updateQuestion: (
    questionId: string,
    updatedData: Partial<Omit<Question, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteQuestionById: (questionId: string) => void;
  loading: Loadings;
  error: Errors;
}

const AdminQuestionContext = createContext<
  AdminQuestionContextProps | undefined
>(undefined);

export const useAdminQuestion = (): AdminQuestionContextProps => {
  const context = useContext(AdminQuestionContext);
  if (!context) {
    throw new Error(
      "useAdminQuestion must be used within a AdminQuestionProvider",
    );
  }
  return context;
};

export const AdminQuestionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(
    null,
  );
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: true,
    details: true,
  });

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!user) return;
      try {
        setLoadings((prev) => ({ ...prev, list: true }));
        const questions = await AdminQuestionService.getAllQuestions();
        setQuestions(questions);
        setLoadings((prev) => ({ ...prev, list: false }));
      } catch (err: any) {
        setErrors((prev) => ({ ...prev, list: err }));
        setLoadings((prev) => ({ ...prev, list: false }));
      }
    };

    fetchQuestions();
  }, [user]);

  const setSelectedQuestionById = async (questionId: string) => {
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const questionData = await AdminQuestionService.getQuestion(questionId);
      setSelectedQuestion(questionData);
      setLoadings((prev) => ({ ...prev, details: false }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, details: err }));
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createQuestion = async (
    questionData: Omit<
      Question,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter" | "updatedBy"
    >,
  ): Promise<string> => {
    const newQuestionId = await AdminQuestionService.createQuestion({
      ...questionData,
      createdBy: user!.uid,
    });
    return newQuestionId;
  };

  const updateQuestion = async (
    questionId: string,
    updatedData: Partial<Omit<Question, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    await AdminQuestionService.updateQuestion(questionId, updatedData);
    await setSelectedQuestionById(questionId);
  };

  const deleteQuestionById = async (questionId: string) => {
    await AdminQuestionService.deleteQuestion(questionId);
    setQuestions((prev) => prev.filter((q) => q._id !== questionId));
    setSelectedQuestion(null);
  };

  return (
    <AdminQuestionContext.Provider
      value={{
        loading: loadings,
        error: errors,
        questions,
        selectedQuestion,
        createQuestion,
        updateQuestion,
        deleteQuestionById,
        setSelectedQuestion: setSelectedQuestionById,
      }}
    >
      {children}
    </AdminQuestionContext.Provider>
  );
};
